import CartAuthenticationDialog from "components/cart/dialogs/CartAuthenticationDialog";
import CartOtpVerficationDialog from "components/cart/dialogs/CartAuthenticationOtpVerificationDialog";
import { ItemCard } from "components/checkout/ItemCard";
import { CheckoutAccordionRefType } from "@shopflo/ui/accordian/CheckoutAccordian";
import { useLocale } from "lib/hooks/useLocale";
import { AddOnMetaType, AddOnType, BannerType, ItemType } from "lib/types/checkout";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Milestone, MilestoneCategory, ProgressBar } from "@shopflo/ui/progress-bar";
import cartImage from "assests/images/cart.png";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useLoadingPerformance } from "lib/hooks/useLoadingPerformance";
import {
  getItemLevelAddOnsForItemIndex,
  getTotalItems,
  hasAnyItemProperties,
  hasAutoAppliedCoupon,
  initializeCart,
  showAddOnLineItems,
} from "lib/utils/checkout";
import { CartHeader } from "components/cart/CartHeader";
import CartNotes from "components/cart/cart-notes/CartNotes";
import Coupons from "components/checkout/Coupons";
import Rewards from "components/checkout/Rewards";
import CombinedProductsRemovalDialog from "components/checkout/dialogs/CombinedProductsRemovalDialog";
import { AddOnItem } from "components/common/AddOnItem";
import CartConfetti from "components/common/confetti/CartConfetti";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import CartBannerStrip from "components/common/notification-strips/CartBannerStrip";
import UpSellAndWishlistSection from "components/upsell/UpSellSection";
import HideInSubscribe from "components/subscriptions/HideInSubscribe";

import { CartFooter } from "components/cart/CartFooter";
import { useToken } from "lib/contexts/TokenProvider";
import CustomError from "lib/core/customError";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { analyticsEvents, analyticsTypes, eventTypes } from "lib/utils/constants";
import {
  inIframe,
  isInstagramiOSBrowser,
  parseMileStoneForEvents,
  publishPostMessage,
} from "lib/utils/helpers";
import CustomComponentSlot from "components/common/CustomComponentSlot";
import { DummyCart } from "./DummyCart";
import Skeleton from "components/common/loaders/Skeleton";
import useAnonymousId from "lib/hooks/useAnonymousId";
import { mutate } from "swr";
import { getUpsellUrl } from "lib/utils/upsell";
import { OrderSummaryConfigType } from "lib/types/merchantConfigTypes";
import { getCardBorderStyles } from "lib/utils/UIUtils";
import { getTextColorByBrightness } from "@shopflo/ui/utils";

// TODO: shradhan This is a temporary solution to handle the cart consequent events.
// We need to find a better way to handle this.
class PromiseQueue {
  private queue: { event: any; resolve: Function; reject: Function }[] = [];
  private isProcessing = false;

  constructor(
    private initialize: (event: any) => Promise<void> | void,
    private logout: (event: any) => Promise<void> | void,
    private login: (event: any) => Promise<void> | void,
    private closeAllModals: (event: any) => Promise<void> | void,
  ) {
    this.initialize = initialize;
    this.logout = logout;
    this.login = login;
  }

  enqueue(event: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.queue.push({ event, resolve, reject });
      this.processNext();
    });
  }

  private async processNext() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;
    const { event, resolve, reject } = this.queue[0];

    try {
      await this.processEvent(event);
      resolve();
    } catch (error) {
      reject(error);
    } finally {
      this.queue.shift();
      this.isProcessing = false;
      this.processNext();
    }
  }

  private async processEvent(event: any) {
    switch (event?.data?.type) {
      case eventTypes.CART_OPEN:
        await this.initialize({ ...event?.data?.payload, ATCTriggered: true });
        break;
      case eventTypes.LOGOUT_FROM_CHECKOUT:
        await this.logout(event);
        break;
      case eventTypes.LOGIN_FROM_CHECKOUT:
        await this.login(event);
        break;
      case eventTypes.CLOSE_ALL_MODALS:
        await this.closeAllModals(event);
        break;
    }
  }

  clear() {
    this.queue = [];
    this.isProcessing = false;
  }

  get length() {
    return this.queue.length;
  }
}

const Cart: React.FC = () => {
  const {
    state: { productType, cartPayload, tokenId, cartJSData },
    actions: { setTokenId, setSegmentId },
  } = useToken();
  const authContext = useAuthContext();
  const {
    state: { isAuthenticated, isLoggedInOnStore },
    actions: { logout, login },
  } = authContext;

  const { closePopup } = usePopupOrchestrator();

  const { initializeAnonymousId } = useAnonymousId();

  const userContext = useUserContext();
  const {
    state: { user },
  } = userContext;

  const checkoutContext = useCheckoutContext();
  const {
    state: {
      checkoutItems,
      billing,
      appliedCoupons,
      checkoutId,
      checkoutModal,
      cartDialog,
      availableAddOns,
      appliedCartAddOns,
    },
    actions: { updateCheckoutBasedOnCheckoutResponse, setCheckoutModal, setCartDialog },
  } = checkoutContext;

  const merchantContext = useMerchantContext();
  const {
    state: { merchant, accountId, cartMetadata },
  } = merchantContext;

  const wishlistConfig = merchant?.wishlistConfig;

  const { progressBarConfig, uiAttributes, primaryCTAConfig } = cartMetadata;

  const analytics = useSendAnalyticsEvent();

  // Use the loading performance hook to track cart loading time
  useLoadingPerformance({
    isLoadingComplete: Boolean(checkoutId),
    eventName: analyticsEvents.FLO_CART_LOADING_TIME_REACT,
  });

  const { t } = useLocale();

  const accordionRef = useRef<CheckoutAccordionRefType>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const isCountBased = useMemo(() => {
    return progressBarConfig?.customizations?.category === MilestoneCategory.COUNT_BASED;
  }, [progressBarConfig?.customizations?.category]);
  const itemCountForProgressBar = useMemo(() => {
    return checkoutItems?.filter((item) => !item.is_freebie).reduce((acc, item) => acc + item.quantity, 0);
  }, [checkoutItems]);

  const priceBasedMilestoneBasis = useMemo(() => {
    if (progressBarConfig?.customizations?.milestoneBasis === "CART_PAYABLE_AFTER_DISCOUNT") {
      return billing?.total_payable;
    }
    return billing?.sub_total;
  }, [progressBarConfig?.customizations?.milestoneBasis, billing?.total_payable, billing?.sub_total]);

  const previousTotalItems = useRef(itemCountForProgressBar);
  const previousTotalPrice = useRef(priceBasedMilestoneBasis);
  const totalItems = getTotalItems(checkoutItems);
  const addOnList = Object.values(availableAddOns ?? {});
  const longSessionId = analytics.getLongSessionId();
  const mileStonesAmount =
    progressBarConfig?.milestones?.map((milestone: any) => Number(milestone.milestone_amount)) ?? [];

  const topBannerConfig = useMemo(() => {
    return cartMetadata?.uiAttributes?.bannerConfig?.find((banner) => banner.type === "CART");
  }, [cartMetadata]);

  const bottomBannerConfig = useMemo(() => {
    return cartMetadata?.uiAttributes?.bottomBannerConfig?.find((banner) => banner.type === "CART");
  }, [cartMetadata]);

  useEffect(() => {
    if (inIframe()) {
      publishPostMessage(eventTypes.SET_CART_ITEM_COUNT, { totalItems });
    }
  }, [totalItems]);

  useEffect(() => {
    previousTotalItems.current = itemCountForProgressBar;
    previousTotalPrice.current = priceBasedMilestoneBasis;
  }, [itemCountForProgressBar, priceBasedMilestoneBasis]);

  useEffect(() => {
    if (inIframe()) {
      publishPostMessage(eventTypes.REMOVE_CART_LOADING_STATE, {});
    }
    setTimeout(() => {
      accordionRef.current?.openAccordion();
    }, 3000);
  }, []);

  useEffect(() => {
    if (!addOnList.length) return;
    const isCartLevelAddOn = addOnList.some(
      (item) => item?.addon_level === "CART" && ["CART", "DEFAULT"].includes(item?.ui_asset),
    );
    const isItemLevelAddOn = addOnList.some((item) => item?.addon_level === "ITEM");
    if (merchant?.merchantId && productType !== "BACKGROUND_CART") {
      analytics.sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_ADDON_LOADED,
        eventFor: [analyticsTypes.SF_ANALYTICS],
        eventType: "load",
        metaData: {
          merchantId: merchant?.merchantId,
          addOnData: {
            view: "CART",
            isCartLevelAddOn,
            isItemLevelAddOn,
          },
        },
      });
    }
  }, [addOnList?.length, merchant?.merchantId, productType]);

  useEffect(() => {
    if (accountId) {
      initialize();
    }
  }, [accountId]);

  useEffect(() => {
    const promiseQueue = new PromiseQueue(
      (payload: Record<string, any>) => initialize(payload),
      () => logout(true),
      (event: any) => {
        if (event?.data?.payload) {
          const { idToken, refreshToken, authExpiry, refreshExpiry } = event.data.payload;
          login(idToken, refreshToken, authExpiry, refreshExpiry);
        }
      },
      () => {
        setCheckoutModal("NONE");
        closePopup();
      },
    );

    const handleMessage = (event: any) => {
      promiseQueue.enqueue(event).catch((error) => {
        console.error("error processing cart event:", error);
      });
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      promiseQueue.clear();
    };
  }, [isLoading, isAuthenticated, checkoutId, closePopup]);

  useEffect(() => {
    initializeAnonymousId(isAuthenticated);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isInstagramiOSBrowser()) {
      return;
    }
    history.pushState({ cart: "ongoing" }, "");
  }, []);

  const backButtonHandler = useCallback(() => {
    if (isInstagramiOSBrowser()) {
      closeCart();
      return;
    }
    if (checkoutModal !== "NONE") {
      setCheckoutModal("NONE");
      history.pushState({ cart: "close_modal" }, "");
      return;
    }
    closeCart();
  }, [checkoutModal]);

  useEffect(() => {
    window.addEventListener("popstate", backButtonHandler);
    return () => {
      window.removeEventListener("popstate", backButtonHandler);
    };
  }, [backButtonHandler]);

  const closeCart = () => {
    setCheckoutModal("NONE");
    closePopup();
    publishPostMessage(eventTypes.CLOSE_CART_IFRAME, {});
  };

  const initialize = async (payload: Record<string, any> = {}) => {
    setIsLoading(true);
    try {
      const response = await initializeCart({
        cartPayload: payload?.cartPayload ?? cartPayload,
        analytics,
        userContext,
        authContext,
        checkoutContext,
        merchantContext,
        isUpdate: Boolean(payload?.cartPayload),
        tokenId: tokenId ?? "",
      });
      if (response !== undefined) {
        const { checkoutResponse, tokenData } = response;
        if (Boolean(checkoutResponse?.segment_id)) setSegmentId({ segmentId: checkoutResponse?.segment_id });
        // This is the same fresh response checkoutInit just dispatched, so its
        // item-level add-on eligibility is authoritative here too - trusting it
        // avoids clobbering what checkoutInit set with stale pre-init state.
        updateCheckoutBasedOnCheckoutResponse(checkoutResponse, "AUTO", true);
        if (Boolean(uiAttributes?.upsell?.isEnabled) && payload?.ATCTriggered) {
          mutate(getUpsellUrl({ checkoutId }));
        }
        setTokenId({ tokenId: tokenData?.uid });
      }
    } catch (error: any) {
      if (error instanceof CustomError) {
        const { err } = error.extraData;
        if (err?.message === "Token refresh failed" || err?.response?.status === 403) {
          logout(true);
          return;
        }

        if (payload?.ATCTriggered) {
          publishPostMessage(eventTypes.CART_LOAD_FAILURE, {
            reason: "ATC_FAIL",
          });
          return;
        }

        publishPostMessage(eventTypes.CART_LOAD_FAILURE, {
          reason: "BG_LOAD_FAIL",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCartPrimaryCTAText = () => {
    return primaryCTAConfig?.ctaText ?? "Checkout";
  };

  const handleMilestoneReached = (milestone: Milestone) => {
    if (productType === "BACKGROUND_CART") return;
    analytics.sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_CHECKOUT_MILESTONE_REACHED,
      eventType: "flo_action",
      metaData: {
        milestone: parseMileStoneForEvents(milestone),
        milestones: progressBarConfig?.milestones?.map((milestone: Milestone) =>
          parseMileStoneForEvents(milestone),
        ),
        progressBarCategory: progressBarConfig?.customizations?.category,
        progressBarType: progressBarConfig?.customizations?.type,
      },
    });
  };

  const elementOrder = uiAttributes?.elementOrder ?? ["ORDER_SUMMARY", "DISCOUNTS", "UPSELL", "NOTES"];

  const renderElement = (element: string) => {
    switch (element) {
      case "ORDER_SUMMARY":
        return (
          <>
            <CustomComponentSlot position="TOP_SECTION" />
            <div
              id="order-summary-section"
              className="bg-zinc-100 rounded-xl p-[10px]"
              style={{
                backgroundColor: uiAttributes?.orderSummaryConfig?.backgroundColor,
                borderRadius: uiAttributes?.orderSummaryConfig?.cardRadius,
                ...getCardBorderStyles({
                  borderSides: uiAttributes?.orderSummaryConfig?.cardBorderSides,
                  borderWidth: uiAttributes?.orderSummaryConfig?.cardBorderWidth,
                  borderColor: uiAttributes?.orderSummaryConfig?.borderColor,
                }),
              }}>
              <CartItems
                items={checkoutItems}
                disableOOSItems={false}
                checkoutItemsMutable={true}
                isUpdating={isLoading}
                setIsUpdating={setIsLoading}
                availableAddOns={availableAddOns}
                cartLevelAddOns={appliedCartAddOns}
                customisations={uiAttributes?.orderSummaryConfig}
                itemPropertiesConfig={uiAttributes?.itemProperties}
                showAllItemProperties={uiAttributes?.layout?.metadata?.showAllItemProperties}
              />
            </div>
            <CustomComponentSlot position="ORDER_SUMMARY_SECTION" />
          </>
        );
      case "DISCOUNTS":
        return (
          <>
            {(hasAutoAppliedCoupon(appliedCoupons) || Boolean(merchant?.isDiscountEnabled)) && (
              <HideInSubscribe>
                <Coupons context="cart" />
                <CustomComponentSlot position="COUPONS_SECTION" />
              </HideInSubscribe>
            )}
            <Rewards context="cart" />
            <CustomComponentSlot position="REWARDS_SECTION" />
          </>
        );
      case "UPSELL":
        return Boolean(uiAttributes?.upsell?.isEnabled) ||
          Boolean(
            isAuthenticated &&
              isLoggedInOnStore &&
              wishlistConfig?.isEnabled &&
              wishlistConfig?.config?.cart?.enabled,
          ) ||
          Boolean(uiAttributes?.recentlyViewedConfig?.enabled) ? (
          <HideInSubscribe>
            <UpSellAndWishlistSection parent="CART" customisation={uiAttributes?.upsell} />
            <CustomComponentSlot position="UPSELL_SECTION" />
          </HideInSubscribe>
        ) : null;
      case "NOTES":
        return <CartNotes customisation={uiAttributes?.notesConfig} />;
      default:
        return null;
    }
  };

  if (!checkoutId && cartJSData) {
    return <DummyCart />;
  }

  if (!checkoutId) {
    return <Skeleton />;
  }

  return (
    <div className="items-between flex h-full flex-col bg-white relative">
      {Boolean(progressBarConfig?.isEnabled) && (
        <CartConfetti
          total={isCountBased ? itemCountForProgressBar : priceBasedMilestoneBasis}
          mileStonesAmount={mileStonesAmount}
          previousTotal={isCountBased ? previousTotalItems.current : previousTotalPrice.current}
          isCountBased={isCountBased}
        />
      )}
      <CartHeader
        totalItems={totalItems}
        customisations={uiAttributes?.header}
        showBottomShadow={!topBannerConfig?.enabled}
      />
      <CartBannerStrip bannerPosition={BannerType.HEADER} bannerConfig={topBannerConfig} />
      <CustomComponentSlot position="HEADER_SECTION" />
      <>
        <div
          className="flex flex-1 flex-col overflow-auto overflow-x-hidden scrollbar-hide"
          ref={mainContentRef}
          style={{
            backgroundColor: uiAttributes?.page?.backgroundColor,
          }}>
          {progressBarConfig?.isEnabled && (
            <>
              <ProgressBar
                total={isCountBased ? itemCountForProgressBar : priceBasedMilestoneBasis}
                milestones={progressBarConfig.milestones}
                customizations={progressBarConfig.customizations}
                onMilestoneReached={handleMilestoneReached}
              />
            </>
          )}
          <div
            className="flex flex-col gap-3 px-3 pt-3 pb-5"
            style={{
              backgroundColor: uiAttributes?.page?.backgroundColor,
            }}>
            {elementOrder.map((element) => {
              if (element !== "ORDER_SUMMARY" && !tokenId) return null;
              return <React.Fragment key={element}>{renderElement(element)}</React.Fragment>;
            })}
          </div>
        </div>
        <CustomComponentSlot position="FOOTER_SECTION" />
        <CartFooter
          setDialog={setCartDialog}
          billing={billing}
          isLoading={isLoading}
          primaryCTAText={getCartPrimaryCTAText()}
          setIsUpdating={setIsLoading}
          bottomBannerConfig={bottomBannerConfig}
        />
        <CartAuthenticationDialog
          open={cartDialog === "authentication" || cartDialog === "cartAuthentication"}
          setDialog={setCartDialog}
        />
        <CartOtpVerficationDialog
          open={cartDialog === "otpVerification" || cartDialog === "cartOtpVerification"}
          setDialog={setCartDialog}
        />
        <GenericDialog
          isOpen={checkoutModal === "COMBINED_PRODUCT_REMOVE"}
          setIsOpen={() => setCheckoutModal("NONE")}
          translateAxis="y"
          customClass="overflow-scroll md:!top-auto md:absolute"
          dialogOverlay={true}
          modalType="COMBINED_PRODUCT_REMOVE">
          <CombinedProductsRemovalDialog />
        </GenericDialog>
      </>
    </div>
  );
};

export default Cart;

function CartItems({
  items,
  disableOOSItems,
  checkoutItemsMutable,
  isUpdating = false,
  setIsUpdating,
  availableAddOns,
  cartLevelAddOns,
  customisations,
  itemPropertiesConfig,
  showAllItemProperties,
}: {
  items: any[];
  disableOOSItems: boolean;
  checkoutItemsMutable: boolean;
  isUpdating?: boolean;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  availableAddOns: Record<string, AddOnMetaType>;
  cartLevelAddOns: AddOnType[];
  customisations?: OrderSummaryConfigType;
  itemPropertiesConfig?: string[];
  showAllItemProperties?: boolean;
}) {
  const { handleAddOnEdit, isLoading } = useItemEdit({
    setIsUpdating,
    isCartItem: true,
  });

  const isItemPropertiesAvailable = hasAnyItemProperties(
    items,
    itemPropertiesConfig ?? [],
    Boolean(showAllItemProperties),
  );

  const isAddOnLineItemsVisible = showAddOnLineItems(availableAddOns, cartLevelAddOns);
  const filteredCartAppliedAddOns = cartLevelAddOns?.filter((item) =>
    ["CART", "DEFAULT"].includes(availableAddOns?.[item?.addon_id]?.ui_asset ?? ""),
  );

  return (
    <ul
      className="flex w-full flex-col gap-5 [&>li]:relative [&>li:not(:last-child)]:after:content-[''] [&>li:not(:last-child)]:after:absolute [&>li:not(:last-child)]:after:left-0 [&>li:not(:last-child)]:after:right-0 [&>li:not(:last-child)]:after:bottom-[-10px] [&>li:not(:last-child)]:after:h-[0.5px] [&>li:not(:last-child)]:after:bg-[var(--divider-color)]"
      style={{
        ["--divider-color" as any]: getTextColorByBrightness({
          bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
          lightOpacity: 24,
        }),
      }}>
      {isAddOnLineItemsVisible && filteredCartAppliedAddOns?.length > 0 && (
        <>
          {filteredCartAppliedAddOns?.map((item) => {
            return (
              <li>
                <AddOnItem
                  key={item?.addon_id}
                  customisations={customisations}
                  disableActions={isLoading}
                  variant="CART"
                  addOnData={availableAddOns[item?.addon_id]}
                  handleAddOnDelete={() =>
                    handleAddOnEdit({
                      action: "DELETE",
                      addOnLevel: "CART",
                      addOnId: item?.addon_id,
                      addOnPrice: item?.price,
                    })
                  }
                  isItemPropertiesAvailable={Boolean(isItemPropertiesAvailable)}
                />
              </li>
            );
          })}
        </>
      )}
      {(() => {
        const variantOccurrence: Record<string, number> = {};
        return items?.map((item: ItemType, index: number) => {
          const variantKey = item.variant_id ?? "";
          const occurrence = variantOccurrence[variantKey] ?? 0;
          variantOccurrence[variantKey] = occurrence + 1;
          const key = item.variant_id ? `${item.variant_id}_${occurrence}` : `${item.item_id}_${index}`;
          return (
            <li key={key}>
              <ItemCard
                {...item}
                disableOOSItems={disableOOSItems}
                checkoutItemsMutable={checkoutItemsMutable}
                isCartItem={true}
                isUpdating={isUpdating}
                setIsUpdating={setIsUpdating}
                itemLevelAddons={getItemLevelAddOnsForItemIndex(
                  availableAddOns,
                  item.itemIndex ?? index,
                  items,
                )}
                customisations={customisations}
                isItemPropertiesAvailable={Boolean(isItemPropertiesAvailable)}
              />
            </li>
          );
        });
      })()}
    </ul>
  );
}
